import cn from 'clsx'
import React, { useMemo } from 'react'
import { useDrop } from 'react-dnd'
import ReactDOM from 'react-dom'
import { bindActionCreators } from 'redux'

import mixpanel from '@disco/api/mixpanel'
import {
  CUSTOM_CHANNEL,
  INBOX_TYPES,
  PERSONAL_LIBRARY,
} from '@disco/constants/GroupTypes'
import { FILE, GROUP, PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import {
  addAggregatedTrackToDisco,
  alterPlaylistGroups,
  alterTrackGroups,
  copyPlaylistToChannel,
} from '@disco/state-management/actions/ExplorerActions'
import { updateGroup } from '@disco/state-management/actions/GroupActions'
import { resetSelection } from '@disco/state-management/actions/SelectionActions'
import { uploadFilesToGroups } from '@disco/state-management/actions/upload-actions'
import { saveUserSortedChannels } from '@disco/state-management/actions/UserActions'
import {
  getGroups,
  getUserSortedChannels,
  makeGetGroupLocks,
} from '@disco/state-management/selectors/groups'
import { getAllSelectedItems } from '@disco/state-management/selectors/selection'
import {
  sendPlaylistDownloadEventThrottled,
  sendTrackDownloadEventThrottled,
} from '@disco/utils/eventsApi'
import { getFilesFromDraggedItem } from '@disco/utils/file-utils'
import { getFolderChannel } from '@disco/utils/groups'
import { getMixpanelDragAndDropProps } from '@disco/utils/mixpanel/drop-tracks'
import moveFromTo from '@disco/utils/moveFromTo'
import { isReadOnlyPlaylist } from '@disco/utils/playlists'
import { isDraggedItemPartOfSelection } from '@disco/utils/selection'
import { isReadOnlyTrack } from '@disco/utils/tracks'

import type { Group } from 'types/group'
import type { SelectableItem } from 'types/selection'

import { dndConnectRef } from 'utils/dnd-connect-ref'

import { DropFilesIndicator } from 'components/ui/drop-indicator'

import { useDispatch, useSelector } from 'hooks/use-redux'
import useSpringLoad from 'hooks/use-spring-load'

type Props = {
  group: Group
  enableGroupDrop?: boolean | null
  children: React.ReactNode
  onFileHover: () => void
}

const GROUP_DROP_ACTIONS = {
  addAggregatedTrackToDisco,
  alterPlaylistGroups,
  alterTrackGroups,
  copyPlaylistToChannel,
  resetSelection,
  saveUserSortedChannels,
  updateGroup,
  uploadFilesToGroups,
}

type GroupDropActions = {
  [K in keyof typeof GROUP_DROP_ACTIONS]: (...args: Array<any>) => any
}

/**
 * Everything {@link groupTarget} needs for one group, so the drop spec stays a
 * plain function of its inputs: every wrapper that drops onto a group (sidebar
 * row, pane body) shares it, and tests can call it with plain objects.
 */
export type GroupDropContext = GroupDropActions & {
  group: Group
  groups: Group[]
  selectedItems: SelectableItem[]
  userSortedChannels: Group[]
  lock_structure?: boolean
  lock_content?: boolean
  enableGroupDrop?: boolean | null
}

type DropMonitor = {
  // the dragged item is a loose bag of optional payloads (track, playlist,
  // group, files), so each branch below narrows it itself
  getItem: () => any
  didDrop: () => boolean
}

type GroupDropState = {
  isOver: boolean
  canDrop: boolean
  draggedItem: any
  isDropHovered: boolean
}

export const GROUP_DROP_ACCEPTED_TYPES = [TRACK, PLAYLIST, GROUP, FILE]

const appNode =
  typeof document !== 'undefined' ? document.getElementById('app') : null

export const getGroupsToRemove = (
  groups: Group[],
  sourceGroup: Group | null | undefined,
) => {
  const parentChannel = sourceGroup
    ? getFolderChannel(groups, sourceGroup)
    : null
  // Dragging out of a channel or folder is a move: remove the item from its
  // source group, unless the source's root channel locks content changes.
  return sourceGroup &&
    sourceGroup.group_type === CUSTOM_CHANNEL &&
    !parentChannel?.lock_content
    ? [sourceGroup]
    : []
}

export const getSourceGroup = (
  groups: Group[],
  draggedItem: Record<string, any>,
): Group | null => {
  if (draggedItem?.sourceGroupId) {
    return groups.find(g => g.id === draggedItem.sourceGroupId) ?? null
  }

  return null
}

/**
 * Shared drop behaviour for group targets. Kept as a `(context, monitor)` pair
 * instead of being inlined in `useDrop` so the pane-body wrapper can reuse the
 * exact same placement semantics with a narrower accept list.
 */
export const groupTarget = {
  canDrop(context: GroupDropContext, monitor: DropMonitor) {
    const item = monitor.getItem()
    const { group, lock_structure, lock_content } = context

    if (group?.permissions) {
      if (!group.permissions.content && !group.permissions.share_link)
        return false
    }

    if (lock_structure && item?.group) return false
    if (lock_content && (item?.track || item?.playlist || item?.files))
      return false
    if (item?.track) return Boolean(item.track.uploaded)

    const isDraggedChannel = !item?.group?.parent
    const willDropOnFolder = group?.parent

    if (item?.group && isDraggedChannel && willDropOnFolder) return false

    return true
  },

  drop(context: GroupDropContext, monitor: DropMonitor) {
    const draggedItem = monitor.getItem()
    const hasDroppedOnChild = monitor.didDrop()
    if (!draggedItem || hasDroppedOnChild) return

    const {
      group,
      groups,
      selectedItems,
      userSortedChannels,
      enableGroupDrop,
    } = context
    const readOnlyTrack = isReadOnlyTrack(draggedItem.track)
    const readOnlyPlaylist = isReadOnlyPlaylist(draggedItem.playlist)
    const partOfSelection = isDraggedItemPartOfSelection(
      selectedItems,
      draggedItem,
    )
    const sourceGroup = getSourceGroup(groups, draggedItem)
    const targetParent = getFolderChannel(groups, group)
    const sourceParent = getFolderChannel(groups, sourceGroup)
    const removeGroups =
      group.group_type === PERSONAL_LIBRARY ||
      (targetParent?.is_auto_tagged && targetParent?.id !== sourceParent?.id)
        ? []
        : getGroupsToRemove(groups, sourceGroup)
    const targetGroups = [group]

    ;(async () => {
      let effectiveTrack = draggedItem.track

      if (readOnlyTrack) {
        const includeNestedTracks = Boolean(sourceGroup)
        effectiveTrack = await context.addAggregatedTrackToDisco({
          track: draggedItem.track,
          originGroupId: sourceGroup?.id,
          includeNestedTracks,
          includeStems: includeNestedTracks,
        })
      }

      if (readOnlyPlaylist) {
        await context.copyPlaylistToChannel(
          draggedItem.playlist,
          targetGroups[0],
          sourceGroup?.id,
        )
        context.resetSelection()
        return
      }

      // do nothing if user drags item to the same group
      if (sourceGroup?.id === group.id) return

      if (selectedItems.length > 0 && partOfSelection) {
        const playlists = selectedItems.filter(item => item.type === PLAYLIST)

        if (playlists.length > 0) {
          context.alterPlaylistGroups(playlists, targetGroups, removeGroups)
        }

        const tracks = selectedItems.filter(
          item => item.type === TRACK && !item.outside_your_territories,
        )

        if (tracks.length > 0) {
          context.alterTrackGroups(tracks, targetGroups, removeGroups)
        }

        selectedItems.forEach(item => {
          const fromInbox =
            draggedItem.sourceGroupType &&
            INBOX_TYPES.includes(draggedItem.sourceGroupType)

          if (fromInbox && item.type === PLAYLIST) {
            playlists.forEach(playlist => {
              sendPlaylistDownloadEventThrottled(
                playlist.playlist_id,
                undefined,
                draggedItem.sourceGroupId,
              )
            })
          } else if (
            fromInbox &&
            item.type === TRACK &&
            !item.outside_your_territories
          ) {
            sendTrackDownloadEventThrottled(item.id, 'in-app', {
              group_id: draggedItem.sourceGroupId,
              playlist_id: draggedItem.sourcePlaylistId,
            })
          }
        })
      } else if (
        effectiveTrack &&
        effectiveTrack.uploaded &&
        !effectiveTrack.outside_your_territories
      ) {
        context.alterTrackGroups([effectiveTrack], targetGroups, removeGroups)

        if (
          draggedItem.sourceGroupType &&
          INBOX_TYPES.includes(draggedItem.sourceGroupType)
        ) {
          sendTrackDownloadEventThrottled(effectiveTrack.id, 'in-app', {
            group_id: draggedItem.sourceGroupId,
            playlist_id: draggedItem.sourcePlaylistId,
          })
        }
      } else if (draggedItem.playlist) {
        context.alterPlaylistGroups(
          [draggedItem.playlist],
          targetGroups,
          removeGroups,
        )

        if (
          draggedItem.sourceGroupType &&
          INBOX_TYPES.includes(draggedItem.sourceGroupType)
        ) {
          sendPlaylistDownloadEventThrottled(
            draggedItem.playlist.playlist_id,
            undefined,
            draggedItem.sourceGroupId,
          )
        }
      } else if (draggedItem.items) {
        getFilesFromDraggedItem(draggedItem).then((files: File[]) => {
          // TODO: Remove this check and handle file limits at a higher level (DISCO-7609)
          const validFiles = files.filter(f => f.size > 0)

          if (validFiles.length > 0 && targetGroups.length > 0) {
            context.uploadFilesToGroups(validFiles, targetGroups)
          }
        })
        return
      }

      if (enableGroupDrop && draggedItem.group) {
        if (!draggedItem.group.parent && draggedItem.group.id !== group.id) {
          const sourceIndex = userSortedChannels.findIndex(
            g => g.id === draggedItem.group.id,
          )
          const targetIndex = userSortedChannels.findIndex(
            g => g.id === group.id,
          )
          const result = moveFromTo(
            userSortedChannels,
            sourceIndex,
            targetIndex,
          )
          context.saveUserSortedChannels(result)
          return
        }

        if (draggedItem.group.parent === group.parent) return

        context.updateGroup(
          {
            ...draggedItem.group,
            image: draggedItem.image === null ? undefined : draggedItem.image,
            parent: group.id,
          },
          true,
        )
      }

      context.resetSelection()

      const eventName = draggedItem?.isPartOfMultipleSelection
        ? 'Drag and drop selected items'
        : 'Drag and drop existing track'

      mixpanel.sendPureEvent(
        eventName,
        getMixpanelDragAndDropProps(
          draggedItem,
          group.group_type === PERSONAL_LIBRARY ? 'Starred' : 'Channel',
        ),
      )
    })()
  },
}

/**
 * Turns a node into a drop target for `group`, running the shared
 * {@link groupTarget} behaviour. Targets only differ in what they accept - a
 * pane body takes content but never groups - and in how they render.
 * @param group - The group items are dropped into
 * @param accept - Item types this target takes
 * @param enableGroupDrop - Whether dropping groups may reorder or reparent them
 */
export const useGroupDrop = (
  group: Group,
  accept: string[],
  enableGroupDrop?: boolean | null,
) => {
  const dispatch = useDispatch()
  const groups = useSelector(getGroups)
  const selectedItems = useSelector(getAllSelectedItems)
  const userSortedChannels = useSelector(getUserSortedChannels)
  const restrictions = useSelector(state => {
    const getGroupLocks = makeGetGroupLocks()
    return getGroupLocks(state, { groupId: group.id })
  })

  const { lock_structure, lock_content } = restrictions ?? {}

  const context: GroupDropContext = useMemo(
    () => ({
      group,
      groups,
      selectedItems,
      userSortedChannels,
      lock_structure,
      lock_content,
      enableGroupDrop,
      // thunk creators are not plain action creators, hence the cast
      ...bindActionCreators(GROUP_DROP_ACTIONS as any, dispatch),
    }),
    [
      dispatch,
      group,
      groups,
      selectedItems,
      userSortedChannels,
      lock_structure,
      lock_content,
      enableGroupDrop,
    ],
  )

  return useDrop<any, unknown, GroupDropState>(
    () => ({
      accept,
      canDrop: (_item, monitor) => groupTarget.canDrop(context, monitor),
      drop: (_item, monitor) => groupTarget.drop(context, monitor),
      collect: monitor => {
        // Shallow so nested drop targets don't all highlight at once.
        const isOver = monitor.isOver({ shallow: true })
        const canDrop = monitor.canDrop()
        return {
          isOver,
          canDrop,
          draggedItem: monitor.getItem(),
          isDropHovered: isOver && canDrop,
        }
      },
    }),
    [accept, context],
  )
}

export default function CustomGroupDropWrapper({
  group,
  enableGroupDrop,
  children,
  onFileHover,
}: Props) {
  const [{ isOver, draggedItem, isDropHovered }, dropRef] = useGroupDrop(
    group,
    GROUP_DROP_ACCEPTED_TYPES,
    enableGroupDrop,
  )

  useSpringLoad(isDropHovered, onFileHover)

  const isChannel = draggedItem?.group && !draggedItem.group.parent
  const isReordering = isOver && isChannel && !group.parent

  const [firstChild, ...rest] = React.Children.toArray(children)
  const wrappedFirstChild = React.cloneElement(
    firstChild as React.ReactElement<{
      isOver?: boolean
      isReordering?: boolean
    }>,
    {
      isOver: !isChannel && !isReordering && isDropHovered,
      isReordering,
    },
  )

  return (
    <div
      ref={dndConnectRef(dropRef)}
      className={cn('GroupDropWrapper', {
        HighlightedDropTarget: isDropHovered,
        'is-over': isDropHovered,
        'is-track': draggedItem && isOver && draggedItem.track,
        'is-playlist': draggedItem && isOver && draggedItem.playlist,
        'is-group': draggedItem && isOver && draggedItem.group,
      })}
    >
      {wrappedFirstChild}
      {rest}
      {isDropHovered &&
        draggedItem?.files &&
        appNode &&
        ReactDOM.createPortal(<DropFilesIndicator />, appNode)}
    </div>
  )
}
